import { useState, useEffect } from "react";
import Skeleton from "../../components/Skeleton";
import Switch from "../../components/Switch";
import { useSnackbar } from "../../components/Snackbar";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import useUpdateFraudMonitoringMutation from "../../hooks/data/mutations/useUpdateFraudMonitoringMutation";
import { AccountHolder } from "../../services/accountService";
import Panel from "./Panel";
import EditControls from "./EditControls";
import HasAccess from "../../components/HasAccess";

type Props = {
  accountConfigData: AccountHolder | undefined;
  isLoading: boolean;
};

function AdyenConfigurations({
  accountConfigData,
  isLoading,
}: Props): JSX.Element {
  const { data: currentUserAccount } = useCurrentUserAccount();
  const {
    mutate: updateFraudMonitoring,
    isLoading: isUpdatingFraudMonitoring,
  } = useUpdateFraudMonitoringMutation(
    accountConfigData?.adyenAccountHolderCode ?? ""
  );
  const [isEditingMonitoring, setIsEditingMonitoring] = useState(false);
  const [fraudMonitoringValue, setFraudMonitoringValue] = useState(
    accountConfigData?.fraudMonitoring ?? false
  );

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  // Sync fraudMonitoringValue when accountConfigData changes
  useEffect(() => {
    setFraudMonitoringValue(
      accountConfigData?.fraudMonitoring === null
        ? false
        : Boolean(accountConfigData?.fraudMonitoring)
    );
  }, [accountConfigData?.fraudMonitoring]);

  const getAdyenAccountHolderDetailUrl = (
    accountHolderId: string | undefined
  ) =>
    process.env.REACT_APP_ADYEN_ACCOUNT_HOLDER_DETAIL_URL_TEMPLATE?.replace(
      "{AccountHolderId}",
      accountHolderId ?? ""
    ).replace(
      "{BalancePlatformKey}",
      process.env.REACT_APP_ADYEN_BALANCEPLATFORM_KEY ?? ""
    ) ?? "";

  return (
    <Panel className="col-span-7">
      <Panel.Header title="Configurations" />
      <Panel.Content>
        <div className="grid grid-cols-2 grid-rows-6 gap-x-2 gap-y-2">
          <div className="font-medium">Payment Processing Status</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : accountConfigData?.paymentProcessingState ? (
              "Enabled"
            ) : (
              "Disabled"
            )}
          </div>
          <div className="font-medium">Merchant Account</div>
          <div className="truncate">
            {isLoading ? <Skeleton /> : accountConfigData?.adyenMerchantAccount}
          </div>

          <div className="font-medium">Payout Status</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : accountConfigData?.payoutState ? (
              "Enabled"
            ) : (
              "Disabled"
            )}
          </div>

          <div className="font-medium">Account Holder</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : currentUserAccount?.type === "CSR" ? (
              <a
                href={getAdyenAccountHolderDetailUrl(
                  accountConfigData?.balancePlatformAccountHolderId
                )}
                title="View Account Holder in Adyen Console"
                target="adyenSite"
                className="text-primary underline"
              >
                {accountConfigData?.adyenAccountHolderCode}
              </a>
            ) : (
              <>{accountConfigData?.adyenAccountHolderCode}</>
            )}
          </div>

          <div className="font-medium">Entity Type</div>
          <div className="truncate">
            {isLoading ? <Skeleton /> : accountConfigData?.legalEntityType}
          </div>

          <div className="font-medium">Account Holder Status</div>
          <div className="truncate">
            {isLoading ? <Skeleton /> : accountConfigData?.adyenStatus}
          </div>

          <div className="font-medium">Payout Schedule</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : (
              accountConfigData?.adyenPayoutSchedule ?? "Active"
            )}
          </div>

          <HasAccess allowedRoles={["ADMIN"]}>
            <>
              <div className="self-center font-medium">Monitoring Status</div>
              <div className="flex min-h-[40px] items-center gap-x-5">
                {isLoading ? (
                  <Skeleton />
                ) : (
                  <Switch
                    checked={fraudMonitoringValue}
                    onChange={() => setFraudMonitoringValue((prev) => !prev)}
                    disabled={!isEditingMonitoring}
                    label=""
                    labelProps={{ className: "font-medium w-100 mr-0" }}
                  />
                )}
                <EditControls
                  customId="editMonitoringStatusButton"
                  isEditing={isEditingMonitoring}
                  isSaving={isUpdatingFraudMonitoring}
                  editButtonProps={{
                    variant: "secondary",
                    onClick: () => setIsEditingMonitoring(true),
                  }}
                  saveButtonProps={{
                    onClick: () => {
                      updateFraudMonitoring(
                        {
                          accountHolderId: accountConfigData?.id ?? "",
                          fraudMonitoring: fraudMonitoringValue,
                        },
                        {
                          onSuccess() {
                            openSnackbar(
                              "Monitoring status was successfully updated.",
                              {
                                type: "success",
                                duration: 3000,
                              }
                            );
                            setIsEditingMonitoring(false);
                          },
                          onError() {
                            openSnackbar(
                              "Failed to update monitoring status. Please try again.",
                              {
                                type: "error",
                                duration: 3000,
                              }
                            );
                          },
                        }
                      );
                    },
                    disabled:
                      accountConfigData?.fraudMonitoring ===
                      fraudMonitoringValue,
                  }}
                  cancelButtonProps={{
                    onClick: () => {
                      setFraudMonitoringValue(
                        accountConfigData?.fraudMonitoring ?? false
                      );
                      setIsEditingMonitoring(false);
                    },
                  }}
                />
              </div>
            </>
          </HasAccess>
        </div>
      </Panel.Content>
    </Panel>
  );
}

export default AdyenConfigurations;
